import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Line } from 'react-chartjs-2';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useBudget } from '../../context/BudgetContext';
import { calculatePayoffTimeline } from './debtCalculations';
import { formatCAD } from '../../utils/formatCurrency';

const COLORS = ['#1E3A5F', '#7D5A00', '#2E7D32', '#7B1FA2', '#C62828', '#00838F'];

export default function DebtPayoffChart() {
  const { t } = useTranslation();
  const { state, derived } = useBudget();
  const chartRef = useRef(null);

  const extraPayment = Math.max(0, derived.monthlyNet * 0.1);
  const result = calculatePayoffTimeline(state.debts, state.debtStrategy, extraPayment);

  if (result.timeline.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
        {t('budgetPlanner.debt.noData', 'Agrega deudas para ver la proyección.')}
      </Typography>
    );
  }

  // Sample every N months if timeline is very long
  const sampleRate = result.timeline.length > 60 ? Math.ceil(result.timeline.length / 60) : 1;
  const sampled = result.timeline.filter((_, i) => i % sampleRate === 0 || i === result.timeline.length - 1);

  const labels = sampled.map((d) => `${t('budgetPlanner.common.month', 'Mes')} ${d.month}`);

  const datasets = state.debts.map((debt, idx) => ({
    label: debt.creditor,
    data: sampled.map((snap) => snap.debts[debt.id] || 0),
    borderColor: COLORS[idx % COLORS.length],
    backgroundColor: 'transparent',
    tension: 0.3,
    fill: false,
    pointRadius: 0,
  }));

  const data = { labels, datasets };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${formatCAD(Math.round(ctx.raw))}`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { callback: (v) => `$${v.toLocaleString('en-CA')}` },
      },
    },
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {t('budgetPlanner.debt.freeIn', 'Libre de deudas en')}: <strong>{result.debtFreeMonth} {t('budgetPlanner.common.months', 'meses')}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('budgetPlanner.debt.totalInterest', 'Interés total')}: <strong>{formatCAD(result.totalInterest)}</strong>
        </Typography>
      </Box>
      <Box sx={{ height: 280, position: 'relative' }}>
        <Line ref={chartRef} data={data} options={options} />
      </Box>
    </Box>
  );
}
